import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SectionHeader from "../SectionHeader";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import NoDocuments from "Assets/Img/NoDocuments";
import { useDocuments } from "hooks/useDocuments";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import Input from "Components/Common/Input/Input";
import Button from "Components/Common/Button/Button";

const CATEGORIES = [
  { value: "pre_construction", label: "Pre-Construction" },
  { value: "csc_report", label: "CSC Report" },
  { value: "draw_backup", label: "Draw Backup" },
  { value: "general", label: "Project Document" },
];

const getIconLabel = (fileName: string) => {
  const ext = (fileName.split(".").pop() ?? "").toLowerCase();
  if (ext === "pdf") return "PDF";
  if (["doc", "docx"].includes(ext)) return "DOC";
  if (["xls", "xlsx", "csv"].includes(ext)) return "XLS";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "IMG";
  return "FILE";
};

const formatGroupKey = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function DocSkeleton() {
  return (
    <div style={{ padding: "12px 0" }}>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Skeleton width={40} height={40} borderRadius={6} />
          <div style={{ flex: 1 }}>
            <Skeleton width={220} height={13} />
            <Skeleton width={160} height={11} style={{ marginTop: 6 }} />
          </div>
          <Skeleton width={100} height={30} borderRadius={6} />
        </div>
      ))}
    </div>
  );
}

interface DocumentsSectionProps {
  isActive: boolean;
}

const DocumentsSection = ({ isActive }: DocumentsSectionProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    documents,
    loading,
    uploading,
    downloadingId,
    handleDownload,
    handleUpload,
  } = useDocuments(projectId, isActive);

  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedCategory, setSelectedCategory] = useState("general");
  const [categoryError, setCategoryError] = useState(false);

  const entries = documents ? Object.entries(documents) : [];
  const totalCount = entries.reduce((sum, [, docs]) => sum + docs.length, 0);

  const handleUploadClick = () => {
    if (uploading) return;
    if (!selectedCategory) {
      setCategoryError(true);
      return;
    }
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file, selectedCategory);
    e.target.value = "";
    // setSelectedCategory("");
    setCategoryError(false);
  };

  return (
    <div className={`section ${isActive ? "active" : ""}`} id="sec-documents">
      <SectionHeader
        title="Documents & Files"
        subtitle="Project files. Upload subcontractor invoices, insurance renewals, and other project documents."
      />

      {/* Upload card */}
      <div className="card">
        <div className="card-title">Upload Document</div>

        {/* <div style={{ marginBottom: 10 }}>
          <Select
            value={selectedCategory}
            options={[
              { value: "", label: "Select document category *" },
              ...CATEGORIES,
            ]}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCategoryError(false);
            }}
            className={"ssel"}
          />
          {categoryError && (
            <div
              style={{
                color: "var(--red, #e53935)",
                fontSize: 12,
                marginTop: 4,
              }}
            >
              Please select a category before uploading.
            </div>
          )}
        </div> */}

        <Input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <div
          className="uploadArea"
          onClick={handleUploadClick}
          role="button"
          tabIndex={0}
          style={{
            opacity: uploading ? 0.6 : 1,
            cursor: uploading ? "not-allowed" : "pointer",
          }}
        >
          <div className="uaIcon">{uploading ? "⏳" : "📎"}</div>
          <div className="uaText">
            {uploading ? "Uploading..." : "Click to upload"}
          </div>
          <div className="uaSub">PDF, JPG, PNG, XLSX · up to 25MB</div>
        </div>
      </div>

      {/* Documents list */}
      {loading ? (
        <DocSkeleton />
      ) : totalCount === 0 ? (
        <EmptyState
          icon={<NoDocuments />}
          title="No Documents Yet"
          subtitle="Project documents will appear here once they are uploaded and published."
        />
      ) : (
        entries.map(([groupKey, docs]) => (
          <div className="card" key={groupKey}>
            <div className="card-title">{formatGroupKey(groupKey)}</div>
            {docs.map((doc) => {
              const uploaderName = [
                doc.uploader?.firstName,
                doc.uploader?.lastName,
              ]
                .filter(Boolean)
                .join(" ");
              const date = doc.addOns?.date
                ? new Date(doc.addOns.date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : new Date(doc.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  });
              const meta = [uploaderName, doc.uploader?.companyName, date]
                .filter(Boolean)
                .join(" · ");

              return (
                <div className="docItem" key={doc.id}>
                  <div className="docIcon">
                    {getIconLabel(doc.value?.fileName ?? "")}
                  </div>
                  <div>
                    <div className="docName">{doc.label}</div>
                    {meta && <div className="docMeta">{meta}</div>}
                  </div>
                  <Button
                    type="button"
                    className="docBtn"
                    disabled={downloadingId === doc.id}
                    onClick={() => handleDownload(doc)}
                  >
                    {downloadingId === doc.id ? (
                      <>
                        <span className="btn-spinner" /> Downloading…
                      </>
                    ) : (
                      "Download"
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
};

export default DocumentsSection;
